import React, { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '../utils/apiUtils';
import { configService } from '../services/configService';
import './VocabularyScreen.css';

export interface Vocable {
  id: string;
  japanese: string;
  kana: string;
  english: string[];
  partOfSpeech?: string;
  verbType?: string;
  transitive?: boolean;
  conjugations?: Record<string, string>;
  username?: string;
  nextReview?: string; // ISO date string or timestamp
  createdAt?: number; // Timestamp in milliseconds
}

interface VocabularyScreenProps {
  onBack: () => void;
  onAddVocable: () => void;
  onViewVocable: (vocable: Vocable) => void;
  refreshToken: number;
}

// Module-level cache for vocabulary
let cachedVocables: Vocable[] | null = null;
let cachedRefreshToken: number | null = null;

// Export function to clear vocabulary cache (e.g., on logout)
export function clearVocabularyCache() {
  cachedVocables = null;
  cachedRefreshToken = null;
}

type SortOption = 'nextReview' | 'alphabetical' | 'dateAdded';
type SortOrder = 'asc' | 'desc';

function VocabularyScreen({ onBack, onAddVocable, onViewVocable, refreshToken }: VocabularyScreenProps) {
  const [vocables, setVocables] = useState<Vocable[]>(cachedVocables ?? []);
  const [isLoading, setIsLoading] = useState(!cachedVocables);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSortDialog, setShowSortDialog] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  const loadVocables = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await authenticatedFetch('/api/vocabulary/vocables');

      if (!response.ok) {
        throw new Error('Failed to fetch vocabulary');
      }

      const data = await response.json();
      cachedVocables = data;
      cachedRefreshToken = refreshToken;
      setVocables(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vocabulary');
    } finally {
      setIsLoading(false);
    }
  }, [refreshToken]);

  // Load sort settings from configuration
  const loadSortSettings = useCallback(async () => {
    try {
      const config = await configService.getConfiguration();
      if (config.sortOption && (config.sortOption === 'nextReview' || config.sortOption === 'alphabetical' || config.sortOption === 'dateAdded')) {
        setSortOption(config.sortOption as SortOption);
      }
      if (config.sortOrder && (config.sortOrder === 'asc' || config.sortOrder === 'desc')) {
        setSortOrder(config.sortOrder as SortOrder);
      }
    } catch (err) {
      // Silently fail - use defaults if config can't be loaded
      console.error('Failed to load sort settings:', err);
    } finally {
      setIsLoadingConfig(false);
    }
  }, []);

  // Save sort settings to configuration
  const saveSortSettings = useCallback(async (option: SortOption | null, order: SortOrder) => {
    try {
      const config = await configService.getConfiguration();
      await configService.saveConfiguration({
        ...config,
        sortOption: option || undefined,
        sortOrder: option ? order : undefined,
      });
    } catch (err) {
      // Silently fail - settings will be lost on refresh but won't break the UI
      console.error('Failed to save sort settings:', err);
    }
  }, []);

  useEffect(() => {
    // Check if we need to fetch: cache doesn't exist or refresh token changed
    if (cachedVocables && cachedRefreshToken === refreshToken) {
      // Use cached data
      setVocables(cachedVocables);
      setIsLoading(false);
    } else {
      // Fetch fresh data
      loadVocables();
    }
  }, [refreshToken, loadVocables]);

  // Load sort settings on mount
  useEffect(() => {
    loadSortSettings();
  }, [loadSortSettings]);

  // Helper function to parse date strings/timestamps
  const parseDate = (dateStr: string): Date | null => {
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date;
      }
      // Try parsing as timestamp (seconds)
      const timestamp = parseInt(dateStr, 10);
      if (!isNaN(timestamp)) {
        const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
        const dateFromTimestamp = new Date(ms);
        if (!isNaN(dateFromTimestamp.getTime())) {
          return dateFromTimestamp;
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  const lowerSearch = searchTerm.toLowerCase();
  const filteredVocables = vocables.filter((v) => {
    const matchesJapanese = v.japanese?.includes(searchTerm);
    const matchesKana = v.kana?.includes(searchTerm);
    const matchesEnglish = v.english?.some((meaning) =>
      meaning.toLowerCase().includes(lowerSearch)
    );
    const matchesConjugations = v.conjugations ? 
      Object.values(v.conjugations).some((conjugation) =>
        conjugation?.toLowerCase().includes(lowerSearch)
      ) : false;
    return Boolean(matchesJapanese || matchesKana || matchesEnglish || matchesConjugations);
  });

  // Sort filtered vocables based on selected sort option and order
  const sortedVocables = [...filteredVocables].sort((a, b) => {
    if (!sortOption) return 0;

    let comparison = 0;

    switch (sortOption) {
      case 'nextReview': {
        // Sort by next review date
        const aDate = a.nextReview ? parseDate(a.nextReview) : null;
        const bDate = b.nextReview ? parseDate(b.nextReview) : null;
        if (!aDate && !bDate) comparison = 0;
        else if (!aDate) comparison = 1;
        else if (!bDate) comparison = -1;
        else comparison = aDate.getTime() - bDate.getTime();
        break;
      }
      case 'alphabetical': {
        // Sort alphabetically by Japanese (kanji) text
        const aText = a.japanese || a.kana || '';
        const bText = b.japanese || b.kana || '';
        comparison = aText.localeCompare(bText, 'ja');
        break;
      }
      case 'dateAdded': {
        // Sort by creation date
        const aDate = a.createdAt || 0;
        const bDate = b.createdAt || 0;
        comparison = aDate - bDate;
        break;
      }
      default:
        return 0;
    }

    // Apply sort order (ascending or descending)
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const handleSort = (option: SortOption) => {
    // If selecting the same option, toggle order; otherwise set new option with default order
    let newOption: SortOption | null = option;
    let newOrder: SortOrder = 'asc';
    
    if (sortOption === option) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newOption = option;
    }
    
    setSortOption(newOption);
    setSortOrder(newOrder);
    
    // Save to configuration
    if (!isLoadingConfig) {
      saveSortSettings(newOption, newOrder);
    }
  };

  const handleAddClick = () => {
    onAddVocable();
  };

  const handleVocableClick = (vocable: Vocable) => {
    onViewVocable(vocable);
  };

  if (isLoading) {
    return (
      <div className="vocabulary-screen">
        <div className="vocabulary-container">
          <div className="loading-state">Loading vocabulary...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="vocabulary-screen">
      <div className="vocabulary-container">
        <div className="vocabulary-header">
          <h2 className="vocabulary-title">Vocabulary</h2>
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="vocabulary-toolbar">
          <input
            type="text"
            className="search-input"
            placeholder="Search vocabulary..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="btn btn-primary" onClick={handleAddClick}>
            Add
          </button>
        </div>
        <div className="vocabulary-filter-row">
          <div className="vocabulary-filter-info">
            {searchTerm
              ? `Showing ${filteredVocables.length} of ${vocables.length} ${vocables.length === 1 ? 'vocable' : 'vocables'}`
              : `${vocables.length} ${vocables.length === 1 ? 'vocable' : 'vocables'}`}
          </div>
          <button 
            className="vocabulary-sort-button"
            onClick={() => setShowSortDialog(true)}
          >
            Sort
          </button>
        </div>

        <div className="vocabulary-list">
          {sortedVocables.length === 0 ? (
            <div className="empty-state">
              {searchTerm ? 'No vocabulary found matching your search.' : 'No vocabulary yet. Start learning!'}
            </div>
          ) : (
            sortedVocables.map((vocable) => (
              <button
                key={vocable.id}
                type="button"
                className="vocable-card"
                onClick={() => handleVocableClick(vocable)}
              >
                <div className="vocable-japanese">{vocable.japanese}</div>
                {vocable.kana !== vocable.japanese && (
                  <div className="vocable-kana">{vocable.kana}</div>
                )}
                <div className="vocable-english">
                  {vocable.english.join(', ')}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Sort Dialog */}
        {showSortDialog && (
          <div 
            className="confirm-overlay" 
            role="dialog" 
            aria-modal="true" 
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowSortDialog(false);
              }
            }}
          >
            <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <h3 className="confirm-title">Sort Vocabulary</h3>
              <div className="sort-options">
                <button
                  className={`sort-option ${sortOption === 'nextReview' ? 'active' : ''}`}
                  onClick={() => handleSort('nextReview')}
                >
                  Next Review Date
                  {sortOption === 'nextReview' && (
                    <span className="sort-order-indicator">
                      {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </button>
                <button
                  className={`sort-option ${sortOption === 'alphabetical' ? 'active' : ''}`}
                  onClick={() => handleSort('alphabetical')}
                >
                  Alphabetically
                  {sortOption === 'alphabetical' && (
                    <span className="sort-order-indicator">
                      {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </button>
                <button
                  className={`sort-option ${sortOption === 'dateAdded' ? 'active' : ''}`}
                  onClick={() => handleSort('dateAdded')}
                >
                  Date Added
                  {sortOption === 'dateAdded' && (
                    <span className="sort-order-indicator">
                      {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </button>
              </div>
              <div className="confirm-actions">
              <button
                  className="btn btn-secondary"
                  onClick={() => setShowSortDialog(false)}
                >
                  Close
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setSortOption(null);
                    setSortOrder('asc');
                    setShowSortDialog(false);
                    // Save to configuration
                    if (!isLoadingConfig) {
                      saveSortSettings(null, 'asc');
                    }
                  }}
                >
                  Clear Sort
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VocabularyScreen;

